// The story episodes: short cartoons of the Greek myths, told in Greek a beginner can follow.
// Each one first teaches its picture words, then tells the story with them; the phrases it
// teaches can be kept for review. This is the light list the app shows; an episode's scene, sound
// and lines load only when it is opened (src/stories/player/).

import type { Persisted } from '../state/model';
import catalog from './generated/catalog.json';

export interface EpisodeInfo {
  id: string;
  season: number;
  number: number;
  title: string;
  titleEn: string;
  premise: string;
  /** Seconds. */
  duration: number;
  poster: string;
  mix: string;
  /** How many phrases can be kept from it. */
  keep: number;
}

export const episodes = catalog as EpisodeInfo[];

export const SEASONS: Record<number, { title: string; titleEn: string }> = {
  1: { title: 'Κρήτη', titleEn: 'Crete' },
};

export function getEpisode(id: string): EpisodeInfo | undefined {
  return episodes.find((e) => e.id === id);
}

/** The episode after this one in its season, if there is one yet. */
export function nextAfter(id: string): EpisodeInfo | undefined {
  const ep = getEpisode(id);
  return ep && episodes.find((e) => e.season === ep.season && e.number === ep.number + 1);
}

/**
 * A new story every two lessons, in order. Each episode builds on the words of the ones before
 * it and on the lessons, so the episode at place i (counting from 0, in the order they are
 * watched) opens once the learner has done lesson 2i + 1 and watched the episode before it to the
 * end. An episode once started stays open for good.
 */
export const LESSONS_PER_EPISODE = 2;

/** The lesson after which an episode opens: 1, 3, 5, … */
export function lessonsNeeded(ep: EpisodeInfo): number {
  return episodes.indexOf(ep) * LESSONS_PER_EPISODE + 1;
}

/** What still stands between the learner and an episode: lessons to do, or the episode before it. */
export interface Lock {
  /** The lesson after which it opens. */
  lesson: number;
  lessonsLeft: number;
  /** The episode to watch to the end first, if that is still to do. */
  after?: EpisodeInfo;
}

/** Why an episode can't be watched yet, or null when it can. */
export function lockOf(state: Persisted, ep: EpisodeInfo): Lock | null {
  if (state.profile.stories?.[ep.id]) return null;
  const lesson = lessonsNeeded(ep);
  const lessonsLeft = Math.max(0, lesson - state.profile.lessonsDone.length);
  const prev = episodes[episodes.indexOf(ep) - 1];
  const after = prev && !state.profile.stories?.[prev.id]?.finished ? prev : undefined;
  return lessonsLeft === 0 && !after ? null : { lesson, lessonsLeft, after };
}

/** "Opens after lesson 7 · 2 lessons to go", or "Opens after episode 3". */
export function lockText(lock: Lock, countdown = true): string {
  if (lock.lessonsLeft > 0) {
    const n = lock.lessonsLeft;
    return `Opens after lesson ${lock.lesson}${countdown ? ` · ${n} ${n === 1 ? 'lesson' : 'lessons'} to go` : ''}`;
  }
  return `Opens after episode ${lock.after!.number}`;
}

/** 'next' is the first episode not watched to the end; 'locked' ones wait for lessons or the episode before. */
export type EpisodeStatus = 'watched' | 'next' | 'later' | 'locked';

export function episodeStatus(state: Persisted, ep: EpisodeInfo): EpisodeStatus {
  if (state.profile.stories?.[ep.id]?.finished) return 'watched';
  if (lockOf(state, ep)) return 'locked';
  return upNext(state)?.id === ep.id ? 'next' : 'later';
}

/** The first episode not yet watched to the end, in order. */
export function upNext(state: Persisted): EpisodeInfo | undefined {
  return episodes.find((e) => !state.profile.stories?.[e.id]?.finished);
}

/** "2 min" */
export function minutes(seconds: number): string {
  return `${Math.max(1, Math.round(seconds / 60))} min`;
}
